import { Button, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import createDialogTitle from '../../../generic/utilities/dialogUtil'
import { SmartDialog } from '../../../generic/utilities/SmartDialog'
import { SaveRounded, WarningRounded } from '@mui/icons-material'
import { useEffect, useRef, useState } from 'react'
import SmartSelect from '../../../generic/smartSelect/SmartSelect'
import { collectCascadePendingItems } from '../cascadeApproval'
import { loadFullWorkflowTree } from '../cascadeWorkflowLoader'
import CascadeIndexChangeDialog from './CascadeIndexChangeDialog'
import { formatCodeName } from '../../../generic/utilities/objectStructures/codeNameFormat'

/**
 * @typedef {import('../../../generic/utilities/objectStructures/workflow/workflow').Workflow} Workflow Workflow object
 */

/**
 *
 * @param {Object} props
 * @param {boolean} props.open Variable to control dialog visibility
 * @param {Function} props.setOpen Function to set dialog visibility
 * @param {Function} props.onSave Function to execute on save, returns overwriteData boolean
 * @param {Function} props.onRevert Function to execute on revert
 * @param {Workflow} props.workflow Current workflow object
 * @param {boolean} props.canPropagate Variable to control workflow propagation availability
 * @param {import('../../../generic/utilities/objectStructures/globalStructures').IdName[]} props.indexValues
 * @param {boolean} props.workflowUseIndex
 * @param {boolean} props.cascadeApprovalEnabled Whether WORKFLOW_CASCADE_APPROVAL is active (S16, only combined with workflowUseIndex, RN05.1)
 * @param {boolean} [props.featureGroupRevisionControlled] Story 186: CONTROLLED feature group revisions; the save cascade then leaves the groups already at or above the target (RN49)
 * @param {boolean} props.savedWorkflowIndexIsApproved
 * @param {boolean} props.isSaving Historia 161 (RN15-RN17): a save from THIS editor is in flight — SAVE stays disabled.
 * @param {Function} props.beginSave Historia 161 (RN15/RN16): synchronous guard. Returns `false` (and does nothing else)
 *   when a save is already in flight; otherwise marks one in flight and returns `true`. Called at the very first
 *   synchronous point of the SAVE click, before the cascade tree load or any other `await` (RN16).
 * @param {Function} props.endSave Historia 161 (RN18): releases the in-flight save. Only needed here for the paths that
 *   abandon the save without ever reaching `onSave` (cancelling a dialog derived from SAVE) — `onSave` (`saveWorkflow`)
 *   releases every other exit itself.
 * @param {Function} props.showAlert Bug found in review (not covered by any signed scenario, RN18): shows the error
 *   when `loadFullWorkflowTree` rejects, the same way `Workflow.jsx#handleOpenSaveDialog` shows any other failure
 *   while preparing the save dialog. Defaults to a no-op so existing mounts that do not pass it keep working.
 * @returns
 */
const WorkflowSaveDialogs = ({ open, setOpen, onSave, onCancel, workflow, canPropagate = false, indexValues, workflowUseIndex, cascadeApprovalEnabled = false, featureGroupRevisionControlled = false, savedWorkflowIndexIsApproved, isSaving = false, beginSave = () => true, endSave = () => {}, showAlert = () => {} }) => {
  const { t_ } = useSmartTranslation()
  // Enmienda 2 (H1/S74, RN35/RN36): whether this component (and therefore its parent editor) is
  // still mounted. `requestSave`'s tree load is one of the two pre-`PUT` phases H1 names — if the
  // editor closes while it is in flight, the chain must be abandoned with no `PUT` once it resolves.
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])
  const [openCheckTaskProgramsDialog, setOpenCheckTaskProgramsDialog] = useState(false)
  const [openCascadeApprovalDialog, setOpenCascadeApprovalDialog] = useState(false)
  const [cascadePendingItems, setCascadePendingItems] = useState([])
  // Fully loaded tree (nested subworkflows included) the cascade will be applied to on accept.
  const [cascadeWorkflow, setCascadeWorkflow] = useState(null)
  const [overwriteData, setOverwriteData] = useState(false)
  const [propagate, setPropagate] = useState(false)
  const [comment, setComment] = useState('')
  const [currentSelectedIndexValue, setCurrentSelectedIndexValue] = useState()
  // Historia 161 (S33): guards against a double click on the ACCEPT button of a dialog derived
  // from SAVE (this component's own re-render — closing the dialog — is not synchronous, so two
  // clicks before repaint would otherwise both fire). One flag per derived dialog, armed when it
  // opens and consumed by its own accept handler; unrelated to `isSaving`, which is already `true`
  // by the time either dialog can even open (RN16).
  const taskProgramsAcceptedRef = useRef(false)
  const cascadeAcceptedRef = useRef(false)

  useEffect(() => {
    setCurrentSelectedIndexValue(indexValues?.find(index => index.name === 'DRAFT'))
    if ((workflow.used && overwriteData) || (savedWorkflowIndexIsApproved && workflowUseIndex)) {
      setOverwriteData(false)
    }
  }, [workflow.id, workflow.used, overwriteData, setCurrentSelectedIndexValue, indexValues, savedWorkflowIndexIsApproved, workflowUseIndex])
  // Same value already sent to onSave before S16 existed — kept unchanged to avoid altering the
  // pre-existing save behavior; only reused here to decide whether to offer the cascade modal.
  const targetIndexValueName = () => currentSelectedIndexValue?.name

  /**
   * S16 (HU-05/RN05.1/RN05.2, enmienda 4 GATE 2) + enmienda PO 23/07/2026 (cascade generalized to
   * ANY target indexValue, not only APPROVED): when both USE_WORKFLOW_INDEX and
   * WORKFLOW_CASCADE_APPROVAL are active and any subworkflow/job/featureGroup of the
   * already-loaded workflow tree differs from the selected index, lists them all and asks for
   * confirmation before saving. If accepted, the caller (`Workflow.jsx#saveWorkflow`) sets all
   * those indexValues to the selected one inside the same save payload (S17, atomicity guaranteed
   * server-side by the existing save transaction). If cancelled, nothing changes (the save dialog
   * stays open with the previous values).
   */
  const requestSave = async () => {
    if (workflowUseIndex && cascadeApprovalEnabled) {
      // Load the whole tree first (nested subworkflows included) so the cascade lists and
      // changes the nodes inside every subworkflow, not only the ones already opened.
      let fullWorkflow
      try {
        fullWorkflow = await loadFullWorkflowTree(workflow)
      } catch (error) {
        // Bug found in review (RN18, no signed scenario): a rejected load left `isSaving` (and,
        // since Enmienda 2, the overlay covering the whole editor) stuck forever — neither `onSave`
        // nor `endSave` ever ran. Released and shown the same way `handleOpenSaveDialog` shows any
        // other failure while preparing the save dialog.
        if (mountedRef.current) {
          showAlert({ title: t_('Error preparing save dialog:'), severity: 'error', message: t_(error.message) })
        }
        endSave()
        return
      }
      // Enmienda 2 (H1/S74, RN36): the editor closed while this pre-`PUT` phase was in flight — the
      // chain is abandoned here, with no `PUT` and no explicit `unLock` (the edit reservation just
      // expires on its own once the ping stops).
      if (!mountedRef.current) {
        return
      }
      const pendingItems = collectCascadePendingItems(fullWorkflow, targetIndexValueName(), { controlled: featureGroupRevisionControlled })
      if (pendingItems.length > 0) {
        // S34/RN16: arm the cascade dialog's own guard before showing it, so its accept can fire
        // exactly once even on a double click before repaint.
        cascadeAcceptedRef.current = false
        setCascadePendingItems(pendingItems)
        setCascadeWorkflow(fullWorkflow)
        setOpenCascadeApprovalDialog(true)
        return
      }
    }
    onSave(overwriteData, propagate, comment, targetIndexValueName(), false)
  }

  /**
   * Method used to check if there are tasks without programs
   * and if there are tasks without tools
   * before saving data
   */
  const checkTaskPrograms = () => {
    let taskWithoutProgram = false
    workflow.nodes.forEach((node) => {
      if (node.nodeType === 'WORKFLOW') {
        return
      }
      node.jobReference.taskReferences.forEach((taskReference) => {
        if (taskReference.defaultProgram === null) {
          taskWithoutProgram = true
        }
      })
    })

    if (taskWithoutProgram) {
      // S33/RN16: same reasoning as the cascade dialog above.
      taskProgramsAcceptedRef.current = false
      setOpenCheckTaskProgramsDialog(true)
    } else {
      requestSave()
    }
  }

  return (
    <>
      <SmartDialog
        setOpen={open}
        title={createDialogTitle({ title: t_('Save data?'), icon: SaveRounded })}
        message={t_('Are you sure you want to save data?')}
        cancel
        accept={false}
        cancelCallback={onCancel}
        renderComponent={
          workflow?.id !== undefined &&
            <>
              <Typography sx={{ mb: 1 }}>
                {t_('There are unsaved changes compared to the original workflow. Do you want to overwrite?')}
              </Typography>
              <Stack direction='row' alignItems='center' gap='1rem' display='inline-flex'>
                <FormControlLabel
                  disabled={workflow.used || (savedWorkflowIndexIsApproved && workflowUseIndex)}
                  control={
                    <Checkbox
                      checked={overwriteData}
                      sx={{ mr: 1, cursor: 'pointer' }}
                      onChange={() => setOverwriteData(!overwriteData)}
                    />
                }
                  label={t_('Overwrite existing workflow data')}
                />
                {workflowUseIndex
                  ? <SmartSelect
                      label={t_('Type')}
                      value={overwriteData ? workflow.indexValue?.name : t_(currentSelectedIndexValue?.name)}
                      onChange={(value) => {
                        const selectedOption = indexValues.find(index => index.name === value)
                        setCurrentSelectedIndexValue(selectedOption)
                      }}
                      selectableOptions={indexValues.map((option) => t_(option.name))}
                      disabled={overwriteData}
                      disableClearable
                      width='8rem'
                      size='small'
                    />
                  : null}
                <FormControlLabel
                  disabled={!canPropagate || (savedWorkflowIndexIsApproved && workflowUseIndex)}
                  control={
                    <Checkbox
                      checked={propagate}
                      sx={{ mr: 1, cursor: 'pointer' }}
                      onChange={() => setPropagate(!propagate)}
                    />
                }
                  label={t_('Propagate')}
                />
              </Stack>
              <div>
                <Typography>{t_('Comment to show on changes history:')}</Typography>
                <textarea
                  style={{ width: '50%', minHeight: '80px', marginTop: '0.5rem' }}
                  placeholder={t_('Add a comment...')}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              </div>
            </>
        }
        renderCustomButtons={
          <>
            <Button
              color='primary'
              disabled={isSaving}
              onClick={() => {
                // S30/RN15/RN16: the in-flight flag is acquired HERE, synchronously, before the
                // cascade tree load or any other await this save chain may run — a second click
                // that lands before repaint sees the lock already taken and does nothing.
                if (!beginSave()) {
                  return
                }
                checkTaskPrograms()
                setOpen(false)
              }}
              variant='outlined'
            >
              <Typography color='primary'>{t_('SAVE')}</Typography>
              <SaveRounded color='primary' sx={{ fontSize: '1.4em', marginLeft: '0.3rem' }} />
            </Button>
          </>
        }
      />

      <SmartDialog
        setOpen={openCheckTaskProgramsDialog}
        message={t_('There are tasks without programs.') + ' ' + t_('Are you sure you want to save data?')}
        title={createDialogTitle({ title: t_('Warning'), icon: WarningRounded, color: 'warning' })}
        accept
        close={false}
        acceptCallback={() => {
          // S33: guards a double click on THIS accept button — `isSaving` is already `true` by now
          // (RN16), so it cannot be reused here to tell the first click from the second.
          if (taskProgramsAcceptedRef.current) {
            return
          }
          taskProgramsAcceptedRef.current = true
          setOpenCheckTaskProgramsDialog(false)
          requestSave()
        }}
        cancelCallback={() => {
          setOpen(false)
          setOpenCheckTaskProgramsDialog(false)
          // RN18: abandoning the save here (never reaching `onSave`) must release the lock too —
          // otherwise the header stays disabled forever (risk R-7).
          endSave()
        }}
      />

      <CascadeIndexChangeDialog
        open={openCascadeApprovalDialog}
        subjectLabel={`${t_('Workflow')} ${formatCodeName(workflow?.code, workflow?.name)}`}
        previousIndexValueName={workflow?.indexValue?.name}
        targetIndexValueName={targetIndexValueName()}
        items={cascadePendingItems}
        onAccept={() => {
          // S33: same double-click guard as the tasks-without-program dialog above.
          if (cascadeAcceptedRef.current) {
            return
          }
          cascadeAcceptedRef.current = true
          setOpenCascadeApprovalDialog(false)
          onSave(overwriteData, propagate, comment, targetIndexValueName(), true, cascadeWorkflow)
        }}
        onCancel={() => {
          setOpenCascadeApprovalDialog(false)
          setCascadeWorkflow(null)
          // RN18: same reasoning as the tasks-without-program dialog's cancel above.
          endSave()
        }}
      />
    </>
  )
}

export default WorkflowSaveDialogs
